import { Link } from "react-router-dom";

import { SafeImage } from "@/components/site/SafeImage";
import { TEAM_MEMBERS, type TeamMember } from "@/lib/team";
import { useSiteConfig, whatsappHref } from "@/lib/site-config";

/**
 * "The People Behind Every Deal." — shared between the homepage and the
 * About page. Renders agent cards from `TEAM_MEMBERS`; until the roster is
 * supplied it shows a personal-introduction block rather than fabricated
 * profiles.
 */
export function Team() {
  const config = useSiteConfig();
  const members = TEAM_MEMBERS;

  return (
    <section
      data-on-dark
      className="border-t border-[rgba(240,234,216,0.10)] bg-[var(--navy-deep)]"
    >
      <div className="container-wide py-14 sm:py-16 md:py-20 short:py-10">
        <div className="grid gap-6 md:grid-cols-12 md:items-end md:gap-12">
          <div className="md:col-span-5">
            <p className="eyebrow !text-[var(--gold-bright)]">Our Team</p>
            <h2 className="mt-4 display-lg max-w-[14ch] text-[var(--text-on-dark)]">
              The People Behind Every Deal.
            </h2>
          </div>
          <p className="max-w-[58ch] text-[14.5px] leading-[1.75] text-[var(--text-on-dark-soft)] sm:text-[15.5px] md:col-span-6 md:col-start-7">
            Our advisors have lived and worked in Dubai for years. They know the
            communities, the developers, and the contracts — and they are
            available directly via WhatsApp.
          </p>
        </div>

        {members.length > 0 ? (
          <ul className="mt-10 grid gap-5 sm:grid-cols-2 md:mt-12 lg:grid-cols-4">
            {members.map((member) => (
              <TeamCard
                key={member.name}
                member={member}
                brandWhatsapp={whatsappHref(config.whatsapp)}
              />
            ))}
          </ul>
        ) : (
          <div className="mt-10 flex flex-col gap-5 border border-[rgba(240,234,216,0.16)] p-7 sm:flex-row sm:items-center sm:justify-between sm:p-9 md:mt-12">
            <p className="max-w-[52ch] text-[15px] leading-[1.7] text-[var(--text-on-dark-soft)] sm:text-[16px]">
              We'll introduce you to the right specialist for your community,
              budget, and timeline — no call centre, no being passed around a
              sales floor.
            </p>
            <Link
              to="/contact"
              className="btn btn-primary btn-block-sm sm:flex-none"
            >
              Meet an Advisor
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
                <path d="M2 7h10m-4-4 4 4-4 4" stroke="currentColor" strokeWidth="1.4" />
              </svg>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}

function TeamCard({
  member,
  brandWhatsapp,
}: {
  member: TeamMember;
  brandWhatsapp: string;
}) {
  const href = member.whatsapp
    ? `https://wa.me/${member.whatsapp.replace(/[^0-9]/g, "")}`
    : brandWhatsapp;

  return (
    <li className="flex flex-col border border-[rgba(240,234,216,0.16)] bg-[rgba(240,234,216,0.03)]">
      <div className="relative aspect-[4/5] overflow-hidden bg-[var(--navy)]">
        {member.photo ? (
          <SafeImage
            src={member.photo}
            alt={member.name}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <span className="absolute inset-0 flex items-center justify-center font-display text-6xl text-[var(--gold-bright)]/40">
            {member.name.charAt(0)}
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-5">
        <h3 className="font-display text-[20px] leading-tight text-[var(--text-on-dark)]">
          {member.name}
        </h3>
        <p className="mt-1 text-[12.5px] uppercase tracking-[0.14em] text-[var(--gold-bright)]">
          {member.title}
        </p>
        <dl className="mt-4 space-y-2 text-[12.5px] text-[var(--text-on-dark-soft)]">
          {member.languages.length > 0 && (
            <div className="flex gap-2">
              <dt className="flex-none uppercase tracking-[0.12em] text-[var(--text-on-dark-soft)]/70">
                Languages
              </dt>
              <dd className="text-[var(--text-on-dark)]">
                {member.languages.join(", ")}
              </dd>
            </div>
          )}
          {member.reraAgentNo && (
            <div className="flex gap-2">
              <dt className="flex-none uppercase tracking-[0.12em] text-[var(--text-on-dark-soft)]/70">
                RERA Agent
              </dt>
              <dd className="text-[var(--text-on-dark)] tabular-nums">
                {member.reraAgentNo}
              </dd>
            </div>
          )}
        </dl>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-5 inline-flex min-h-11 items-center justify-center gap-2 border border-[rgba(240,234,216,0.28)] px-4 text-[11px] uppercase tracking-[0.16em] text-[var(--cream)] transition-colors hover:border-[var(--gold-bright)] hover:text-[var(--gold-bright)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--gold-bright)]"
        >
          <svg viewBox="0 0 24 24" className="size-3.5" fill="currentColor" aria-hidden>
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
          </svg>
          WhatsApp {member.name.split(" ")[0]}
        </a>
      </div>
    </li>
  );
}
